/**
 * ``/workspace/templates/$kind`` — per-kind editor surface.
 *
 * Split-view: list on the left (every template of this kind), editor on
 * the right (form for the selected template). Built-ins are read-only —
 * the "Duplica come custom" CTA at the top of the editor lets the user
 * fork a copy. Custom (user/org) rows expose save + delete.
 *
 * Why the editor lives directly in the route file rather than its own
 * component: the form-state lifecycle (loading detail → local edits →
 * save) is tightly coupled to the URL state (which kind + which slug).
 * Hoisting it to a separate component would only introduce prop drilling
 * with no real reuse — the route is the only place this composition
 * exists today.
 *
 * Preview endpoint: the BE proxies a static PNG via
 * ``/api/v1/presentation-templates/_preview/<key>`` but there's no live
 * "render-on-save" endpoint yet. We surface the existing ``preview_url``
 * thumbnail on each card and leave a TODO marker below for the Wave 3
 * async preview-generation work flagged in
 * ``presentation_templates.py:create_template``.
 */

import * as React from "react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { createFileRoute, notFound, useNavigate, useParams } from "@tanstack/react-router";
import { Copy, Save, Trash2 } from "lucide-react";

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  SurfaceEmptyState,
} from "@ando/ui";
import { getTemplate, type TemplateKind, type TemplateSummary } from "@ando/core";

import {
  useTemplates,
} from "@/features/studio/hooks";
import {
  CreateTemplateDialog,
  DEFAULT_TEMPLATE_SPECS,
  SchemaFormEditor,
  TemplateCard,
  useCreateTemplate,
  useDeleteTemplate,
  useTemplateDetail,
  useUpdateTemplate,
} from "@/features/templates";

const VALID_KINDS: ReadonlyArray<TemplateKind> = ["chrome", "paper", "writing_style"];

export const Route = createFileRoute("/_authed/workspace/templates/$kind")({
  parseParams: (params) => {
    if (!VALID_KINDS.includes(params.kind as TemplateKind)) {
      throw notFound();
    }
    return { kind: params.kind as TemplateKind };
  },
  component: TemplatesEditor,
});

function templateIdentifier(t: TemplateSummary): string {
  return t.slug ?? t.name;
}

function TemplatesEditor() {
  const { i18n } = useLingui();
  const { kind } = useParams({ from: "/_authed/workspace/templates/$kind" });
  const navigate = useNavigate();
  const { templates, isLoading, isError, refetch } = useTemplates(kind);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [createOpen, setCreateOpen] = React.useState(false);

  // Reset the selection when the kind changes — a chrome slug never
  // resolves under a paper route. Picking the first row in the new list
  // is convenient but optional; we keep it simple and let the user
  // explicitly click.
  React.useEffect(() => {
    setSelectedId(null);
  }, [kind]);

  // Auto-select the first row once the list lands so the editor doesn't
  // flash an empty state for the common "happy path".
  React.useEffect(() => {
    if (selectedId == null && templates.length > 0) {
      const first = templates[0];
      if (first) setSelectedId(templateIdentifier(first));
    }
  }, [selectedId, templates]);

  const selected = templates.find((t) => templateIdentifier(t) === selectedId) ?? null;

  return (
    <div className="flex h-full min-h-0 gap-4 pt-4">
      {/* Left column — list */}
      <div className="flex w-72 shrink-0 flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-semibold text-[12px] text-ink-2 uppercase tracking-wider">
            {i18n._(msg`Template`)}
          </h3>
          <Button size="xs" onClick={() => setCreateOpen(true)}>
            {i18n._(msg`Nuovo template`)}
          </Button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto pr-1">
          {isLoading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-14 animate-pulse rounded-lg border border-hairline/40 bg-card/30"
              />
            ))
          ) : isError ? (
            <div className="flex flex-col gap-2 rounded-md border border-hairline bg-card/40 p-3 text-[12px] text-ink-2">
              <span>{i18n._(msg`Errore nel caricamento dei template.`)}</span>
              <Button
                size="xs"
                variant="outline"
                onClick={() => {
                  void refetch();
                }}
              >
                {i18n._(msg`Riprova`)}
              </Button>
            </div>
          ) : templates.length === 0 ? (
            <SurfaceEmptyState
              title={i18n._(msg`Nessun template`)}
              description={i18n._(msg`Crea il primo template personalizzato per iniziare.`)}
              action={
                <Button size="sm" onClick={() => setCreateOpen(true)}>
                  {i18n._(msg`Crea template`)}
                </Button>
              }
            />
          ) : (
            templates.map((t) => {
              const id = templateIdentifier(t);
              return (
                <TemplateCard
                  key={`${t.kind}:${id}`}
                  template={t}
                  active={id === selectedId}
                  onSelect={() => setSelectedId(id)}
                />
              );
            })
          )}
        </div>
      </div>

      {/* Right column — editor */}
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto rounded-lg border border-hairline bg-card/40 p-4">
        {selected ? (
          <EditorPanel
            kind={kind}
            summary={selected}
            onDuplicated={(newSlug) => setSelectedId(newSlug)}
            onDeleted={() => setSelectedId(null)}
          />
        ) : (
          <SurfaceEmptyState
            title={i18n._(msg`Seleziona un template`)}
            description={i18n._(
              msg`Scegli un template dalla lista per iniziare a modificarlo, oppure creane uno nuovo.`,
            )}
          />
        )}
      </div>

      <CreateTemplateDialog
        kind={kind}
        open={createOpen}
        onOpenChange={setCreateOpen}
        inheritSources={templates}
        resolveInheritSpec={async (id) => {
          try {
            const detail = await getTemplate(id);
            return detail.spec;
          } catch {
            return null;
          }
        }}
        onCreated={(slug) => {
          setSelectedId(slug);
          // Sub-route already matches the kind — no navigation needed.
          void navigate({
            to: "/workspace/templates/$kind",
            params: { kind },
          });
        }}
      />
    </div>
  );
}

interface EditorPanelProps {
  readonly kind: TemplateKind;
  readonly summary: TemplateSummary;
  /** Called after "Duplica come custom" creates a fork. */
  readonly onDuplicated: (newSlug: string) => void;
  /** Called after a delete succeeds. */
  readonly onDeleted: () => void;
}

function EditorPanel({
  kind,
  summary,
  onDuplicated,
  onDeleted,
}: EditorPanelProps) {
  const { i18n } = useLingui();
  const identifier = summary.slug ?? summary.name;
  const detailQuery = useTemplateDetail(identifier);
  const updateTpl = useUpdateTemplate();
  const deleteTpl = useDeleteTemplate();
  const createTpl = useCreateTemplate();

  const [spec, setSpec] = React.useState<Record<string, unknown> | null>(null);
  const [name, setName] = React.useState(summary.name);
  const [description, setDescription] = React.useState(summary.description);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
  const [savedFlash, setSavedFlash] = React.useState(false);

  // Sync local form state with the freshly loaded detail. We deliberately
  // depend on the resolved spec reference so re-fetches after a save don't
  // clobber in-flight user edits — the mutation invalidates the query, the
  // refetch lands, the new spec becomes the new baseline.
  React.useEffect(() => {
    if (detailQuery.data) {
      setSpec(detailQuery.data.spec);
      setName(detailQuery.data.name);
      setDescription(detailQuery.data.description);
    }
  }, [detailQuery.data]);

  const readOnly = summary.scope === "builtin";

  async function handleSave() {
    if (!summary.slug || readOnly || spec == null) return;
    await updateTpl.mutateAsync({
      slug: summary.slug,
      body: {
        name,
        description,
        spec,
      },
    });
    setSavedFlash(true);
    window.setTimeout(() => setSavedFlash(false), 1800);
  }

  async function handleDelete() {
    if (!summary.slug || readOnly) return;
    await deleteTpl.mutateAsync(summary.slug);
    setConfirmDeleteOpen(false);
    onDeleted();
  }

  async function handleDuplicate() {
    if (spec == null) return;
    const duplicated = await createTpl.mutateAsync({
      name: i18n._(msg`${summary.name} (copia)`),
      description: summary.description,
      kind,
      spec,
    });
    if (duplicated.slug) {
      onDuplicated(duplicated.slug);
    }
  }

  if (detailQuery.isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-ink-3 text-sm">
        {i18n._(msg`Caricamento template…`)}
      </div>
    );
  }
  if (detailQuery.isError || spec == null) {
    return (
      <SurfaceEmptyState
        title={i18n._(msg`Errore`)}
        description={i18n._(msg`Impossibile caricare il template selezionato.`)}
        action={
          <Button size="sm" onClick={() => void detailQuery.refetch()}>
            {i18n._(msg`Riprova`)}
          </Button>
        }
      />
    );
  }

  return (
    <>
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {readOnly ? (
            <>
              <h2 className="truncate font-semibold text-[18px] text-ink-1">
                {summary.name}
              </h2>
              <p className="text-[12px] text-ink-3">{summary.description}</p>
            </>
          ) : (
            <div className="flex flex-col gap-2">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={64}
                aria-label={i18n._(msg`Nome del template`)}
                className="w-full rounded-md border border-hairline bg-card px-2.5 py-1.5 font-semibold text-[15px] text-ink-1 focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                minLength={10}
                maxLength={500}
                rows={2}
                aria-label={i18n._(msg`Descrizione del template`)}
                className="w-full rounded-md border border-hairline bg-card px-2.5 py-1.5 text-[12px] text-ink-2 focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {readOnly ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => void handleDuplicate()}
              disabled={createTpl.isPending}
            >
              <Copy size={12} aria-hidden />
              {i18n._(msg`Duplica come custom`)}
            </Button>
          ) : (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setConfirmDeleteOpen(true)}
                disabled={deleteTpl.isPending}
              >
                <Trash2 size={12} aria-hidden />
                {i18n._(msg`Elimina`)}
              </Button>
              <Button
                size="sm"
                onClick={() => void handleSave()}
                disabled={updateTpl.isPending}
              >
                <Save size={12} aria-hidden />
                {updateTpl.isPending
                  ? i18n._(msg`Salvataggio…`)
                  : savedFlash
                    ? i18n._(msg`Salvato`)
                    : i18n._(msg`Salva`)}
              </Button>
            </>
          )}
        </div>
      </header>

      <SchemaFormEditor
        kind={kind}
        spec={spec}
        onChange={setSpec}
        readOnly={readOnly}
      />

      {readOnly ? (
        <p className="text-[11px] text-ink-3">
          {i18n._(
            msg`I template built-in sono sola lettura. Usa "Duplica come custom" per creare una versione modificabile.`,
          )}
        </p>
      ) : null}

      {/* TODO Wave 3: when the BE ships ``POST
          /presentation-templates/_preview`` we wire a debounced 800ms
          render call here and surface the PNG inline. Today we lean on
          the static preview_url shipped per card; live preview is
          intentionally NOT implemented to avoid coupling the editor to
          an endpoint that doesn't exist yet. */}

      {/* Custom CONFIRM dialog (no shared ConfirmDialog in @ando/ui yet). */}
      <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{i18n._(msg`Eliminare il template?`)}</DialogTitle>
            <DialogDescription>
              {i18n._(
                msg`L'azione è irreversibile. Il template "${summary.name}" verrà rimosso e non sarà più disponibile nei drawer Studio.`,
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmDeleteOpen(false)}
              disabled={deleteTpl.isPending}
            >
              {i18n._(msg`Annulla`)}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => void handleDelete()}
              disabled={deleteTpl.isPending}
            >
              {deleteTpl.isPending ? i18n._(msg`Eliminazione…`) : i18n._(msg`Elimina`)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

